import type { InitRequest, RequestMessage, ResponseMessage } from './protocol';

export type Handler = (req: RequestMessage, source: MessageEventSource) => Promise<ResponseMessage>;

// startListener wires postMessage → handler with origin enforcement. The first
// allowed origin comes from the project's `init` message itself: the parent
// proves it owns one of the configured origins by being able to reach a fresh
// flow. This is paired with a server-side allowlist check on /v1/wallets/*.
//
// On every event we double-check that event.source matches the parent that
// sent the init, and we reply only to that same source.
//
// We also broadcast `iframe_ready` to the parent window every 200ms until the
// first `init` arrives. Without this, mobile Safari frequently loses the
// init message: the SDK fires it on iframe `load`, but React inside the iframe
// hasn't mounted the listener yet, so the message is dropped. The heartbeat
// lets the SDK wait for proof that the iframe is listening before it sends.
export function startListener(handler: Handler): { destroy: () => void; setBoundOrigin: (origin: string) => void } {
  let boundSource: MessageEventSource | null = null;
  let boundOrigin: string | null = null;
  let initSeen = false;

  function setBoundOrigin(origin: string) {
    boundOrigin = origin;
  }

  async function onMessage(event: MessageEvent) {
    if (!event.source) return;
    if (boundSource && event.source !== boundSource) return;
    if (boundOrigin && event.origin !== boundOrigin) return;

    const data = event.data as Partial<RequestMessage> | undefined;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string' || typeof data.requestId !== 'string') {
      return;
    }

    if (data.type === 'init') {
      boundSource = event.source;
      boundOrigin = event.origin;
      initSeen = true;
    }

    try {
      const res = await handler(data as RequestMessage, event.source);
      reply(event.source, event.origin, res);
    } catch (err) {
      reply(event.source, event.origin, {
        type: 'error',
        requestId: (data as InitRequest).requestId,
        code: 'handler_failed',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  window.addEventListener('message', onMessage);

  // Broadcast iframe_ready until init arrives (or a 30s safety cap).
  // targetOrigin '*' is intentional: this carries no secrets, and we don't
  // know the parent origin until init binds it.
  const startedAt = Date.now();
  const heartbeat = window.setInterval(() => {
    if (initSeen || Date.now() - startedAt > 30_000) {
      window.clearInterval(heartbeat);
      return;
    }
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'iframe_ready' }, '*');
    }
  }, 200);
  // Fire one immediately so fast parents don't wait the full 200ms.
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'iframe_ready' }, '*');
  }

  return {
    destroy: () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(heartbeat);
    },
    setBoundOrigin,
  };
}

function reply(source: MessageEventSource, targetOrigin: string, msg: ResponseMessage) {
  // Only post back to the source that asked us; never wildcard.
  (source as Window).postMessage(msg, { targetOrigin });
}
